import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Building2, Users, Briefcase, MessageCircle, Megaphone, AlertCircle, Trophy, XCircle, Clock, CircleDot, TrendingUp } from "lucide-react";
import { supabase } from "../lib/supabase";
import { fmtNum, fmtBRL0 } from "../lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";

interface LeadDay {
  date: string;
  rd_crm_count: number;
  omni_count: number;
  meta_leads_count: number;
  total: number;
  by_source: Record<string, unknown> | null;
}

interface FunnelRow { total: number; ganhos: number; perdidos: number; abertos: number; parados: number; ganho_valor_brl: number }
interface StageRow { stage: string; abertos: number }
interface SourceRow { canal: string; total: number; ganhos: number; perdidos: number; abertos: number; valor_ganho_brl: number }

// Data de hoje em YYYY-MM-DD no fuso Recife (mesmo bucket usado nas views).
function todayRecife(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Recife", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

export default function LeadsComercial() {
  const [rows, setRows] = useState<LeadDay[]>([]);
  const [funnel, setFunnel] = useState<FunnelRow | null>(null);
  const [stages, setStages] = useState<StageRow[]>([]);
  const [bySrc, setBySrc] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const since = new Date(Date.now() - 90 * 86400_000).toISOString().slice(0, 10);
      const [leadsRes, funnelRes, stagesRes, srcRes] = await Promise.all([
        supabase.from("leads_comercial_daily").select("*").gte("date", since).order("date", { ascending: true }),
        supabase.from("v_rd_crm_funnel").select("*").maybeSingle(),
        supabase.from("v_rd_crm_stages").select("*"),
        supabase.from("v_rd_crm_by_source").select("*"),
      ]);
      setRows((leadsRes.data as LeadDay[]) ?? []);
      setFunnel((funnelRes.data as FunnelRow) ?? null);
      setStages((stagesRes.data as StageRow[]) ?? []);
      setBySrc((srcRes.data as SourceRow[]) ?? []);
      setLoading(false);
    };
    load();
    const ch = supabase
      .channel("leads_comercial_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "leads_comercial_daily" }, load)
      .subscribe();
    return () => { ch.unsubscribe(); };
  }, []);

  const { today, month, series } = useMemo(() => {
    const t = todayRecife();
    const mp = t.slice(0, 7); // YYYY-MM
    const zero = { rd_crm_count: 0, omni_count: 0, meta_leads_count: 0, total: 0 };
    const today = rows.find((r) => r.date === t) ?? { date: t, ...zero, by_source: null } as LeadDay;
    const month = rows.filter((r) => r.date.startsWith(mp)).reduce((a, r) => ({
      rd_crm_count: a.rd_crm_count + (r.rd_crm_count || 0),
      omni_count: a.omni_count + (r.omni_count || 0),
      meta_leads_count: a.meta_leads_count + (r.meta_leads_count || 0),
      total: a.total + (r.total || 0),
    }), { ...zero });
    const series = rows.map((r) => ({
      date: r.date.slice(5), // MM-DD
      total: r.total || 0,
      rd: r.rd_crm_count || 0,
      omni: r.omni_count || 0,
      meta: r.meta_leads_count || 0,
    }));
    return { today, month, series };
  }, [rows]);

  return (
    <>
      <PageHeader
        title="Leads Comercial"
        subtitle="Leads que chegam ao time Comercial — RD CRM + WhatsApp/Chat Esmeralda + Meta Lead Ads · últimos 90 dias"
        icon={<Building2 size={22} strokeWidth={2} />}
      />

      {/* KPIs: total + por fonte (hoje / mês) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <HeroKpi label="Total no mês" month={month.total} today={today.total} icon={<Users size={18} />} />
        <SourceKpi label="RD CRM" sub="Oportunidades no funil" month={month.rd_crm_count} today={today.rd_crm_count} icon={<Briefcase size={16} />} color="#1F9C44" />
        <SourceKpi label="WhatsApp" sub="Chat Esmeralda (omni)" month={month.omni_count} today={today.omni_count} icon={<MessageCircle size={16} />} color="#25D366" />
        <SourceKpi label="Meta Lead Ads" sub="Formulários de campanha" month={month.meta_leads_count} today={today.meta_leads_count} icon={<Megaphone size={16} />} color="#1877F2" />
      </div>

      {/* Nota sobre semântica do total */}
      <div className="mb-6 flex items-start gap-3 bg-esm-cream-50 border border-esm-brown-100 rounded-2xl p-4 text-[12px] text-esm-brown-900">
        <AlertCircle size={16} className="text-esm-brown-700 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-bold mb-0.5">Como contamos</p>
          <p className="leading-relaxed">
            <b>RD CRM</b> = oportunidades (deals) criadas no funil do Comercial. <b>WhatsApp</b> = conversas do Chat Esmeralda
            assumidas por um humano do time de reservas (fica baixo até o Chat ter volume real). <b>Meta Lead Ads</b> = formulários
            de lead das campanhas. O <b>total</b> soma os canais e <b>pode contar a mesma pessoa em mais de um canal</b> (sem
            deduplicação por enquanto). Atualizado 1×/dia.
          </p>
        </div>
      </div>

      <Card title="Leads → Comercial por dia" subtitle="Total diário · últimos 90 dias">
        <div className="h-72">
          {loading ? (
            <div className="h-full w-full bg-esm-cream-50 rounded animate-pulse" />
          ) : series.length === 0 ? (
            <div className="h-full flex items-center justify-center text-esm-gray-400 italic text-sm">Sem dados ainda.</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DB" />
                <XAxis dataKey="date" fontSize={10} stroke="#9B8E7D" />
                <YAxis fontSize={10} stroke="#9B8E7D" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E3DB", fontSize: 12 }} />
                <Line type="monotone" dataKey="total" name="Total" stroke="#966E4D" strokeWidth={2.5} dot={{ r: 2, fill: "#966E4D" }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="rd" name="RD CRM" stroke="#1F9C44" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="omni" name="WhatsApp" stroke="#25D366" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="meta" name="Meta" stroke="#1877F2" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Funil do Comercial (RD CRM) — acompanhamento do lead: virou venda? parou? */}
      <div className="mt-8 mb-3 flex items-center gap-2">
        <TrendingUp size={18} className="text-esm-brown-700" />
        <h3 className="text-sm font-bold text-esm-gray-900">Funil do Comercial · RD CRM</h3>
        <span className="text-[11px] text-esm-gray-400">acompanhamento das oportunidades (estado atual)</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <FunnelStat label="Em aberto" value={fmtNum(funnel?.abertos ?? 0)} sub="no funil" icon={<CircleDot size={16} />} color="#966E4D" />
        <FunnelStat label="Ganhos" value={fmtNum(funnel?.ganhos ?? 0)} sub={fmtBRL0(funnel?.ganho_valor_brl ?? 0)} icon={<Trophy size={16} />} color="#1F9C44" />
        <FunnelStat label="Perdidos" value={fmtNum(funnel?.perdidos ?? 0)} sub="oportunidades" icon={<XCircle size={16} />} color="#DC2626" />
        <FunnelStat label="Parados +21d" value={fmtNum(funnel?.parados ?? 0)} sub="sem atividade" icon={<Clock size={16} />} color="#D4A24A" />
        <FunnelStat
          label="Taxa de ganho"
          value={funnel && (funnel.ganhos + funnel.perdidos) > 0 ? `${Math.round((funnel.ganhos / (funnel.ganhos + funnel.perdidos)) * 100)}%` : "—"}
          sub="dos fechados"
          icon={<TrendingUp size={16} />}
          color="#7A5230"
        />
      </div>
      <Card title="Em aberto por estágio" subtitle="Pipeline ativo do Comercial">
        {stages.length === 0 ? (
          <p className="text-sm text-esm-gray-400 italic py-6 text-center">Sem oportunidades em aberto.</p>
        ) : (
          <div className="space-y-2.5">
            {stages.map((s) => {
              const max = Math.max(...stages.map((x) => x.abertos), 1);
              return (
                <div key={s.stage} className="flex items-center gap-3">
                  <span className="text-xs text-esm-gray-700 w-48 flex-shrink-0 truncate" title={s.stage}>{s.stage}</span>
                  <div className="flex-1 h-5 bg-esm-cream-50 rounded-md overflow-hidden">
                    <div className="h-full bg-esm-brown-500 rounded-md transition-all" style={{ width: `${(s.abertos / max) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold tabular-nums w-10 text-right text-esm-gray-900">{s.abertos}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Origem do lead → resultado" subtitle="De onde veio e quanto virou venda (RD CRM)" padded={false} className="mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-esm-cream-50">
              <tr className="text-left text-esm-gray-500 text-[11px] uppercase tracking-wider">
                <th className="py-3 px-5">Canal</th>
                <th className="py-3 px-5 text-right">Leads</th>
                <th className="py-3 px-5 text-right">Ganhos</th>
                <th className="py-3 px-5 text-right">Perdidos</th>
                <th className="py-3 px-5 text-right">Em aberto</th>
                <th className="py-3 px-5 text-right">Taxa ganho</th>
                <th className="py-3 px-5 text-right">Valor ganho</th>
              </tr>
            </thead>
            <tbody>
              {bySrc.map((r) => {
                const fech = r.ganhos + r.perdidos;
                const wr = fech > 0 ? Math.round((r.ganhos / fech) * 100) : null;
                return (
                  <tr key={r.canal} className="border-t border-esm-gray-100">
                    <td className="py-3 px-5 font-bold text-esm-gray-900">{r.canal}</td>
                    <td className="py-3 px-5 text-right tabular-nums">{fmtNum(r.total)}</td>
                    <td className="py-3 px-5 text-right tabular-nums text-emerald-700">{fmtNum(r.ganhos)}</td>
                    <td className="py-3 px-5 text-right tabular-nums text-red-600">{fmtNum(r.perdidos)}</td>
                    <td className="py-3 px-5 text-right tabular-nums text-esm-gray-700">{fmtNum(r.abertos)}</td>
                    <td className="py-3 px-5 text-right tabular-nums font-bold">{wr === null ? "—" : `${wr}%`}</td>
                    <td className="py-3 px-5 text-right tabular-nums font-bold text-esm-brown-700">{fmtBRL0(r.valor_ganho_brl)}</td>
                  </tr>
                );
              })}
              {bySrc.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-esm-gray-400 italic">Sem dados.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-3 text-[11px] text-esm-gray-400 leading-relaxed">
        Funil e origem vêm do <b>RD CRM</b> (a espinha do acompanhamento do Comercial). ⚠️ <b>"Site" é catch-all</b>: muito lead de
        Meta/Google cai ali porque o formulário do site não guarda a origem do anúncio, então <b>Meta/Google ficam subcontados</b>.
        Pra atribuição precisa seria passar UTM/click-id pro RD (ou dedup por e-mail/telefone com o Meta Lead Ads) — próxima frente.
      </p>
    </>
  );
}

function HeroKpi({ label, month, today, icon }: { label: string; month: number; today: number; icon: ReactNode }) {
  return (
    <div className="rounded-2xl p-5 bg-gradient-to-br from-esm-brown-700 to-esm-brown-900 text-white shadow-card-hover">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-esm-cream-100/80">{label}</p>
        <span className="text-esm-cream-100/70">{icon}</span>
      </div>
      <p className="font-display text-[40px] tabular-nums leading-none">{fmtNum(month)}</p>
      <p className="text-[11px] text-esm-cream-100/70 mt-2">Hoje: <b className="text-white">{fmtNum(today)}</b></p>
    </div>
  );
}

function SourceKpi({ label, sub, month, today, icon, color }: { label: string; sub: string; month: number; today: number; icon: ReactNode; color: string }) {
  return (
    <div className="rounded-2xl p-5 bg-white border border-esm-gray-100 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${color}1A`, color }}>{icon}</span>
          <div>
            <p className="text-[12px] font-bold text-esm-gray-900 leading-tight">{label}</p>
            <p className="text-[10px] text-esm-gray-400 leading-tight">{sub}</p>
          </div>
        </div>
      </div>
      <p className="font-display text-[32px] tabular-nums leading-none text-esm-gray-900">{fmtNum(month)}</p>
      <p className="text-[11px] text-esm-gray-400 mt-2">no mês · hoje: <b className="text-esm-gray-700">{fmtNum(today)}</b></p>
    </div>
  );
}

function FunnelStat({ label, value, sub, icon, color }: { label: string; value: string; sub?: string; icon: ReactNode; color: string }) {
  return (
    <div className="rounded-2xl p-4 bg-white border border-esm-gray-100 shadow-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${color}1A`, color }}>{icon}</span>
        <p className="text-[11px] font-bold uppercase tracking-wider text-esm-gray-400">{label}</p>
      </div>
      <p className="font-display text-[28px] tabular-nums leading-none text-esm-gray-900">{value}</p>
      {sub && <p className="text-[11px] text-esm-gray-400 mt-1.5 truncate">{sub}</p>}
    </div>
  );
}
